import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { SectionLabel, Reveal } from "./ui";

const steps = [
  {
    n: "01",
    title: "Tell us what you're building",
    panelTitle: "Content Direction",
    items: ["Your audience", "Your goals", "Your content style"],
  },
  {
    n: "02",
    title: "Send your footage",
    panelTitle: "Footage",
    items: ["Raw footage", "Brand assets", "References"],
  },
  {
    n: "03",
    title: "We edit & refine",
    panelTitle: "Editing",
    items: ["Motion graphics", "Sound design", "Captions"],
  },
  {
    n: "04",
    title: "You publish",
    panelTitle: "Ready to publish",
    items: ["Final video", "Platform-ready formats", "Final delivery"],
  },
];

export const Process = () => {
  const [active, setActive] = useState(0);
  const step = steps[active];

  return (
    <section id="process" className="relative py-24 md:py-32 px-6 md:px-10" style={{ background: "var(--surface-color)" }}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-14 md:mb-20 max-w-2xl">
          <SectionLabel>How It Works</SectionLabel>
          <Reveal>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
              Four steps. That's it.
            </h2>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left: steps */}
          <div className="flex flex-col">
            {steps.map((s, i) => (
              <button
                key={s.n}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className="group text-left border-b py-6 md:py-8 transition-colors duration-300"
                style={{ borderColor: "var(--border-color)" }}
                data-testid={`process-step-${i}`}
              >
                <div className="flex items-center gap-5 md:gap-8">
                  <span
                    className="font-heading text-sm font-medium tabular-nums transition-colors duration-300"
                    style={{ color: active === i ? "var(--accent-color)" : "var(--text-secondary)" }}
                  >
                    {s.n}
                  </span>
                  <span
                    className="font-heading text-2xl md:text-4xl font-medium tracking-tight transition-all duration-300"
                    style={{
                      color: active === i ? "var(--text-primary)" : "var(--text-secondary)",
                      transform: active === i ? "translateX(6px)" : "translateX(0)",
                    }}
                  >
                    {s.title}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Right: animated panel */}
          <div className="lg:sticky lg:top-28">
            <div
              className="relative rounded-2xl border p-8 md:p-12 min-h-[340px] md:min-h-[420px] flex flex-col justify-between overflow-hidden"
              style={{ borderColor: "var(--border-color)", background: "var(--bg-color)" }}
            >
              <span
                className="pointer-events-none absolute -right-8 -top-10 font-heading text-[9rem] md:text-[13rem] font-bold leading-none opacity-[0.05] select-none"
              >
                {step.n}
              </span>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step.n}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="relative z-10"
                >
                  <span className="text-xs uppercase tracking-[0.35em]" style={{ color: "var(--accent-color)" }}>
                    {step.panelTitle}
                  </span>
                  <ul className="mt-8 space-y-5">
                    {step.items.map((it, idx) => (
                      <motion.li
                        key={it}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + idx * 0.08 }}
                        className="flex items-center gap-4 text-xl md:text-2xl font-heading font-light"
                      >
                        <span
                          className="flex h-7 w-7 items-center justify-center rounded-full border shrink-0"
                          style={{ borderColor: "var(--accent-color)" }}
                        >
                          <Check size={15} style={{ color: "var(--accent-color)" }} />
                        </span>
                        {it}
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
